import React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { useSelector } from 'react-redux';
import { Button, List } from '@pella/sash-framework';

import * as HitComp from '../../../../page/search/hit-comps';

const IndexResults = ({ indexTitle, hitComponent, showCta = true, showRecommendedTitle, className, resultsClassName, noResultsClassName }) => {
	const configData = useStaticQuery(query).contentstackSearchConfig;
	const tiles = useSelector((state) => state.ecommData?.tiles);
	const { results } = useHits();
	const { indexUiState } = useInstantSearch();

	return (
		<div className={`indexResults flex h-full flex-col ${className || ''}`}>
			<p className='mb-2.5 text-sm uppercase tracking-wider text-neutral-600'>
				<span>{indexTitle}</span>
				<span className='ml-1'>({results.nbHits} results)</span>
			</p>

			{results.nbHits > 0 && (
				<div className='indexResults-wrapper overflow-hidden'>
					<List
						bullets='none'
						className={resultsClassName || ''}
					>
						{results?.hits.map((hit) => (
							<li
								key={hit.objectID}
								className='mb-2.5 md:mb-7'
							>
								{hitComponent === 'ProductHitLink' && hit.product_tile && (
									<HitComp.ProductHitLink
										hit={hit}
										data={tiles?.find((tile) => tile.backend_name === hit.backend_name)?.price || null}
									/>
								)}
								{hitComponent === 'ProductHitLink' && !hit.product_tile && <HitComp.HitLink hit={hit} />}
								{hitComponent === 'HitLink' && <HitComp.HitLink hit={hit} />}
								{hitComponent === 'NoImageHitLink' && <HitComp.NoImageHitLink hit={hit} />}
							</li>
						))}
					</List>
				</div>
			)}

			{results.nbHits === 0 && (
				<div className={`indexResults-noResults ${noResultsClassName || ''}`}>
					<p className='font-semibold'>{configData.no_results}</p>

					{showRecommendedTitle && <h3 className='mb-2.5 mt-7 uppercase md:mt-10 lg:mb-5 lg:mt-12'>{configData.recommended_title}</h3>}

					<List
						bullets='none'
						layout='inline'
					>
						{configData?.recommended?.recommended_item?.[0] &&
							configData?.recommended?.recommended_item.map((item, index) => (
								<li
									key={index}
									className='mr-2.5 mt-2.5 w-auto max-w-full'
								>
									<Link
										to={`/search/?query=${item}`}
										className='block bg-[#fff39a] px-3 py-2 text-sm capitalize leading-snug text-neutral-750 no-underline hover:bg-primary sm:text-base'
									>
										{item}
									</Link>
								</li>
							))}
					</List>
				</div>
			)}

			{showCta && results.nbHits > 4 && (
				<Button
					color='primary'
					href={`/search/?query=${indexUiState.query}&category=${indexTitle}`}
					className='mb-7 mt-auto sm:mb-10 lg:mb-0'
					wide
				>
					See All {indexTitle} Results ({results.nbHits})
				</Button>
			)}
		</div>
	);
};

IndexResults.propTypes = {
	indexTitle: PropTypes.string.isRequired,
	hitComponent: PropTypes.string.isRequired,
	showCta: PropTypes.bool,
	showRecommendedTitle: PropTypes.bool,
	className: PropTypes.string,
	resultsClassName: PropTypes.string,
	noResultsClassName: PropTypes.string,
};

export const query = graphql`
	{
		contentstackSearchConfig {
			recommended_title
			recommended {
				recommended_item
			}
			no_results
		}
	}
`;

export default IndexResults;
